import { Directive, ElementRef, Input, OnInit, OnDestroy, AfterViewInit, Optional, Self } from '@angular/core';
import { TranslationService } from '../../service/translation-service/translation.service';
import { Subscription } from 'rxjs';
import { NgSelectComponent } from '@ng-select/ng-select';

@Directive({
  selector: '[appI18n],[appI18nPlaceholder],[appI18nTitle],[appI18nAlt]'
})
export class I18nDirective implements OnInit, OnDestroy, AfterViewInit {
  @Input('appI18n') i18n: string;
  @Input('appI18nPlaceholder') i18nPlaceholder: string;
  @Input('appI18nTitle') i18nTitle: string;
  @Input('appI18nAlt') i18nAlt: string;

  private sub: Subscription;
  private originalText: string;
  private originalPlaceholder: string;
  private originalTitle: string;
  private originalAlt: string;

  constructor(
    private el: ElementRef,
    private translationService: TranslationService,
    @Optional() @Self() private ngSelect: NgSelectComponent
  ) {}

  ngOnInit() {
    this.originalPlaceholder =
      (this.ngSelect && this.ngSelect.placeholder) ||
      this.el.nativeElement.getAttribute('placeholder') ||
      '';
    this.originalTitle = this.el.nativeElement.getAttribute('title') || '';
    this.originalAlt = this.el.nativeElement.getAttribute('alt') || '';
  }

  ngAfterViewInit() {
    this.originalText = this.el.nativeElement.textContent || '';

    this.sub = this.translationService.locale$.subscribe(() => {
      this.translate();
    });
  }

  translate() {
    // 1. Text translation
    if (this.i18n !== undefined) {
      let key = this.i18n || '';
      if (key.startsWith('@@')) {
        key = key.substring(2);
      }
      if (key) {
        const translated = this.translationService.getTranslation(key);
        if (translated) {
          this.el.nativeElement.textContent = translated;
        } else {
          this.el.nativeElement.textContent = this.originalText;
        }
      }
    }

    // 2. Placeholder translation (native inputs + ng-select @Input)
    if (this.i18nPlaceholder !== undefined) {
      let key = this.i18nPlaceholder || '';
      if (key.startsWith('@@')) {
        key = key.substring(2);
      }
      if (key) {
        const translated = this.translationService.getTranslation(key);
        const value = translated || this.originalPlaceholder;
        this.setPlaceholder(value);
      }
    }

    // 3. Title translation
    if (this.i18nTitle !== undefined) {
      let key = this.i18nTitle || '';
      if (key.startsWith('@@')) {
        key = key.substring(2);
      }
      if (key) {
        const translated = this.translationService.getTranslation(key);
        if (translated) {
          this.el.nativeElement.setAttribute('title', translated);
        } else {
          this.el.nativeElement.setAttribute('title', this.originalTitle);
        }
      }
    }

    // 4. Alt translation
    if (this.i18nAlt !== undefined) {
      let key = this.i18nAlt || '';
      if (key.startsWith('@@')) {
        key = key.substring(2);
      }
      if (key) {
        const translated = this.translationService.getTranslation(key);
        if (translated) {
          this.el.nativeElement.setAttribute('alt', translated);
        } else {
          this.el.nativeElement.setAttribute('alt', this.originalAlt);
        }
      }
    }
  }

  private setPlaceholder(value: string) {
    const el = this.el.nativeElement;
    el.setAttribute('placeholder', value);
    try {
      el.placeholder = value;
    } catch (e) { /* ignore */ }

    // ng-select uses @Input() placeholder — attribute updates alone do not refresh it
    if (this.ngSelect) {
      this.ngSelect.placeholder = value;
      if (typeof (this.ngSelect as any).detectChanges === 'function') {
        (this.ngSelect as any).detectChanges();
      }
    }
  }

  ngOnDestroy() {
    if (this.sub) {
      this.sub.unsubscribe();
    }
  }
}
